/**
 * App.tsx 관련 유틸리티 함수
 */

import type { Restaurant } from '@/types';
import { getBottomNavigationTranslations } from '@/components/bottom-navigation/constants';

export interface HeaderTitleParams {
  showPickupMenu: boolean;
  showTableOrderMenu: boolean;
  selectedRestaurant: Restaurant | null;
  activeTab: string;
  // #536 — AppContext.language 는 legacy 3언어 값이므로 full locale 을 전달한다.
  locale: string;
  t: {
    qrScanTitle: string;
    homeTitle: string;
    restaurants: Record<string, { name: string }>;
  };
}

/**
 * 현재 상태에 따른 헤더 타이틀 결정
 */
export function getHeaderTitle({
  showPickupMenu,
  showTableOrderMenu,
  selectedRestaurant,
  activeTab,
  locale,
  t,
}: HeaderTitleParams): string {
  // 픽업/테이블 메뉴 모드일 때
  if ((showPickupMenu || showTableOrderMenu) && selectedRestaurant) {
    return t.restaurants[selectedRestaurant.nameKey]?.name ?? '';
  }

  // BottomNavigation 와 동일한 i18n 매핑 재사용 — RECOGIDA 오표시 회귀 차단 (#513)
  // 및 ru 등 full locale 이 legacy language='es' 로 축약되는 회귀 차단 (#536).
  const tabs = getBottomNavigationTranslations(locale).tabs;

  // 탭별 타이틀
  switch (activeTab) {
    case 'qr':
      return t.qrScanTitle;
    case 'points':
      return tabs.points;
    case 'admin':
      return tabs.admin;
    case 'orders':
      return tabs.orders;
    default:
      return t.homeTitle;
  }
}

/**
 * #541 — 익명 순수 메뉴판 모드(?mode=view) 판정.
 *
 * QRVerifyPage 가 view 타입 QR 응답 시 `/restaurant/{id}?mode=view` 로 진입시킨다.
 * 이 모드에서는 전역 하단 네비(TabNav) + 로그인/계정 진입 헤더를 미렌더한다.
 *
 * `/restaurant` 경로로 스코프해 다른 경로가 향후 `?mode=` 쿼리를 사용해도 영향이 없도록 한다.
 *
 * @param pathname 현재 location.pathname
 * @param modeParam searchParams.get('mode') 값 (없으면 null)
 * @returns view 전용 모드 여부
 */
export function isViewOnlyMode(pathname: string, modeParam: string | null): boolean {
  return pathname.startsWith('/restaurant') && modeParam === 'view';
}

/**
 * #781 — 줄서기(Fila Virtual) 경로 여부.
 *
 * 이 화면군(`/fila/{storeSlug}`)은 QR 전용 진입 화면으로 자체 AppBar(매장명 + 15개
 * 언어 선택)와 자체 하단 CTA 를 갖는다. 전역 헤더/하단 네비를 겹쳐 렌더하면 헤더가
 * 이중이 되고, 아직 주문 맥락이 없는 대기 고객에게 장바구니/탭 동선이 노출된다.
 *
 * 하위 경로가 아니라 정확히 한 세그먼트만 매칭한다 — 향후 `/fila/{slug}/{something}`
 * 가 생기면 그 화면이 전역 크롬을 원할지 별도로 판단해야 한다.
 *
 * @param pathname 현재 location.pathname
 */
export function isWaitlistPath(pathname: string): boolean {
  return /^\/fila\/[^/]+\/?$/.test(pathname);
}

/**
 * 줄서기 폼은 자체 `dvh` 레이아웃을 사용하므로 상위 앱 셸이 legacy `100vh` 로 더
 * 길어지면 안 된다. 다른 경로의 기존 높이 계약은 그대로 보존한다.
 */
export function getAppShellViewportClasses(isWaitlistRoute: boolean): {
  root: string;
  main: string;
} {
  return isWaitlistRoute
    ? {
        root: 'min-h-screen supports-[height:100dvh]:min-h-dvh',
        main: 'min-h-0',
      }
    : {
        root: 'min-h-screen',
        main: 'min-h-[calc(100vh-3.5rem)]',
      };
}

/**
 * 줄서기 진입 경로 헬퍼 — QR 인코딩 URL(`/fila/{storeSlug}?p={qrSlug}`)과 동일 형식.
 *
 * @param storeSlug 매장 슬러그
 * @param qrSlug QR 배치 slug (등록 소스 어트리뷰션). 없으면 쿼리 미부착.
 */
export function getWaitlistPath(storeSlug: string, qrSlug?: string): string {
  return qrSlug ? `/fila/${storeSlug}?p=${encodeURIComponent(qrSlug)}` : `/fila/${storeSlug}`;
}

/**
 * #434 codex R1/R2 — `?mode=view` 쿼리의 직접 진입 방어 (defense in depth).
 *
 * /checkout, /cart 같은 주문 경로에 view 쿼리가 직접 입력되면 차단한다.
 * 앱이 생성하는 정상 주문 링크는 mode 쿼리를 전파하지 않으므로 무회귀.
 */
export function hasViewModeQuery(search: string): boolean {
  return new URLSearchParams(search).get('mode') === 'view';
}

/**
 * #514 — 매장 변경 시 navigate 대상 경로 계산.
 *
 * 박제 [feedback_silent_pass_recurrence] 정합 — table 모드에서 URL 패턴 매칭 실패 시
 * silent navigate 차단을 위해 null 반환 (호출처가 경고 + state-only 처리 결정).
 *
 * @returns navigate 대상 경로. table 모드 매칭 실패 시 null (호출처가 차단 처리).
 */
export function getStoreSwitchPath(
  mode: 'pickup' | 'table' | undefined,
  currentPath: string,
  storeId: string,
): string | null {
  if (mode === 'table') {
    // CodeRabbit Major — strict matching. `/foo/table/12` 같은 비정상 path 차단.
    const tableMatch = currentPath.match(/^\/restaurant\/[^/]+\/table\/(\d+)$/);
    if (!tableMatch) {
      return null;
    }
    return `/restaurant/${storeId}/table/${tableMatch[1]}`;
  }
  return `/restaurant/${storeId}`;
}

/**
 * 장바구니 총합 계산
 */
export function calculateCartTotal(items: { price: number; quantity: number }[]): number {
  return items.reduce((total, item) => total + item.price * item.quantity, 0);
}

/**
 * 배달비 계산 (30,000원 이상 무료)
 */
export function calculateDeliveryFee(subtotal: number, freeThreshold = 30000, fee = 3000): number {
  return subtotal >= freeThreshold ? 0 : fee;
}

/**
 * 주문 번호 생성
 */
export function generateOrderNumber(): string {
  return `ORD${String(Math.floor(1 + Math.random() * 999)).padStart(3, '0')}`;
}

/**
 * UUID 생성 (crypto.randomUUID 폴리필 포함)
 * 브라우저 호환성 및 SSR 환경 지원
 */
export function generateUUID(): string {
  // crypto.randomUUID가 사용 가능하면 사용
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // 폴리필: crypto.getRandomValues 기반
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // UUID v4 형식으로 변환
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // 최후의 폴백: Math.random 기반 (보안성 낮음, 테스트용)
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * 모달이 열려있는지 확인
 * 참고: Cart/Notifications/Settings/Map/Logout 모달은 AppModals 내부에서 관리됨
 * 현재는 paymentFlow만 체크 (결제 플로우 중일 때 blur 처리)
 */
export function isAnyModalOpen(modals: {
  paymentFlow: string;
}): boolean {
  return modals.paymentFlow !== 'idle';
}
